import { ChangeDetectorRef, Component, Injector, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
    BillingContactResources,
    BillingContactType,
    BillingContactUsingType,
    IBillingContactResource,
    IBillingContactType,
} from '../../billing-contact-type';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { BillingContactModel } from '../../billing-contact.model';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AppTextService } from '@fi-shared/app-text';
import { getDefaultCountryId } from '@fi-app/core/country-id';
import { ICountryModel } from '@fi-shared/fi-contacts/fi-countries-select/country';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { interval, Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { removeItem } from '@fi-shared/utils/remove-item';
import { FiCustomValidatorPatterns } from '@fi-shared/fi-custom-validator-patterns/fi-custom-validator-patterns';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { IFiContactData } from '@fi-shared/fi-contacts/fi-contacts';

@Component({
    selector: 'fi-billing-contacts',
    templateUrl: './billing-contacts.component.html',
    styleUrls: ['./billing-contacts.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: BillingContactsComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: BillingContactsComponent,
        },
    ],
    host: { class: 'fi-billing-contacts' },
    standalone: false,
})
export class BillingContactsComponent
    extends ControlValueAccessorValidatorBase<BillingContactModel[]>
    implements OnInit, OnDestroy
{
    @Input() usingType: BillingContactUsingType;

    resources: IBillingContactResource;
    billingContactTypes: { Id; Name }[];

    contactData: IFiContactData = this.fiContactsService.getContactData(() => this.ngModel);

    private countryCodesMap: {
        [key: number]: ICountryModel;
    } = {};
    FiCustomValidatorPatterns = FiCustomValidatorPatterns;

    constructor(
        private injector: Injector,
        private appTextService: AppTextService,
        private userDataService: FiUserDataService,
        private detectDevicesService: DetectDevicesService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private fiContactsService: FiContactsService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.setInitialContactsData();
        this.subscribeOnResize();
    }

    private subscribeOnResize() {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => this.cdr.markForCheck());
    }

    isMobileSmall(): boolean {
        return this.detectDevicesService.isMobileSmall();
    }

    private setInitialContactsData() {
        this.resources = BillingContactResources[this.usingType || BillingContactUsingType.Contact];
        this.billingContactTypes = [];

        this.resources.BillingContactTypes.forEach((contactType: IBillingContactType) => {
            this.billingContactTypes.push({
                Id: contactType.Id,
                Name: this.appTextService.get(contactType.TextId),
            });
        });
    }

    addNewContact() {
        const contactToAdd = new BillingContactModel();
        contactToAdd.BillingContactType = BillingContactType.OtherContact;
        contactToAdd.CountryId = getDefaultCountryId(this.userDataService.applicationLanguage);
        contactToAdd.CountryCode = this.GetCountryCode(contactToAdd.CountryId);
        this.ngModel.push(contactToAdd);

        this.doPropagateChanges();
    }

    removeContact(contactToRemove: BillingContactModel) {
        removeItem(this.ngModel, contactToRemove);
        this.doPropagateChanges();
    }

    isDefaultContact(contact: BillingContactModel) {
        return this.ngModel.length === 1 || contact.IsDefault;
    }

    isRequired(contact) {
        return (
            this.isDefaultContact(contact) ||
            (contact.Name && contact.Name.length) > 0 ||
            (contact.Email && contact.Email.length) > 0 ||
            (contact.Phone && contact.Phone.length) > 0
        );
    }

    private GetCountryCode(countryId: number) {
        return this.countryCodesMap[countryId]?.CountryCode;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
